<template>
  <Dialog
      id="registration-modal"
      v-model:visible="visible"
      :header="t('CONFIRM_REGISTRATION')"
      modal
      class="registration-modal"
      :draggable="false"
      :dismissable-mask="true"
  >
    <div class="row">
      <div class="col col-md-6 d-flex flex-column">
        <Card v-if="hasGroupData && isUserProfileLoaded" class="h-50 mb-3">
          <template #title>
            {{ groupData.title }} - {{ groupData.training.translations[locale] }}
          </template>
          <template #content>
            <div class="row align-items-stretch">
              <div class="col-4">
                <p class="fw-bold">{{ t("LOCATION") }}:</p>
                <p class="fw-bold" v-if="hasGroupAddress">{{ t("ADDRESS") }}:</p>
                <p class="fw-bold">{{ t("LANGUAGE") }}:</p>
                <p class="fw-bold">{{ t(dateLabelKey) }}:</p>
                <div v-if="isSingleEvent">
                  <p class="fw-bold">{{ t("TIME") }}:</p>
                  <p class="fw-bold" v-if="hasMultipleWeekdays">{{ t("DAYS") }}:</p>
                </div>
              </div>
              <div class="col">
                <p>{{ groupData.location }}</p>
                <p>{{ groupData.address }}</p>
                <p>{{ groupData.language }}</p>
                <p>{{ getGroupDates(groupData.events) }}</p>
                <div v-if="isSingleEvent">
                  <p>
                    {{
                      t("DAY_AT_TIME", {
                        day: groupData.groupedEvents[0].day,
                        time: groupData.groupedEvents[0].times.join(", "),
                      })
                    }}
                  </p>
                </div>
              </div>
            </div>
          </template>
        </Card>

        <Card class="h-50" v-if="isUserProfileLoaded">
          <template #title>
            <span class="expectations-title">{{ t("EXPECTATIONS") }}</span>
          </template>
          <template #content>
            <Textarea
                class="w-100"
                id="textarea"
                v-model="expectations"
                rows="10"
                :placeholder="t('WRITE_STH')"
            />
          </template>
        </Card>
      </div>

      <div class="col col-md-6 d-flex flex-column">
        <profile-form ref="profileFormRef" class="h-100" @update:validity="isValid = $event" v-show="isUserProfileLoaded"/>
      </div>
    </div>

    <template #footer>
      <div class="w-100 text-end">
        <Button :label="t('REGISTER')" icon="pi pi-check" :disabled="!isValid || !isUserProfileLoaded" @click="groupRegister"/>
      </div>
    </template>
  </Dialog>
</template>

<script setup>
import moment from "moment-timezone";
import ProfileForm from "../forms/ProfileForm.vue";
import {computed, ref} from "vue";
import {injectAxios} from "@/util/inject";
import {useI18n} from "vue-i18n";
import {useToast} from "@/util/toastUtils";
import {ModuleType} from "@/model/module-type";
import {useDateFormat} from "@/mixins/dateFormatMixin2.ts";

const emit = defineEmits(["handleSuccess"]);

const {t, locale} = useI18n();
const toast = useToast();
const {dateFormat} = useDateFormat();
const axios = injectAxios();
const visible = ref(false);
const isValid = ref(false);
const profileFormRef = ref(null);
const groupData = ref(null);
const expectations = ref(null);

const isSingleEvent = computed(() => groupData.value?.training?.type === ModuleType.SINGLE);
const hasGroupData = computed(() => Boolean(groupData.value));
const hasGroupAddress = computed(() => Boolean(groupData.value?.address));
const hasMultipleWeekdays = computed(() => weekdayNames(groupData.value?.events ?? []).length > 1);
const dateLabelKey = computed(() => (isSingleEvent.value ? "DATE" : "DATES"));

function showRegistrationModal(group) {
  groupData.value = group;
  visible.value = true;
}

function getGroupDates(events) {
  if (!events?.length) {
    return "";
  }

  if (events.length === 1) {
    return dateFormat(events[0].startTime);
  }

  const first = events[0];
  const last = events.at(-1);
  if (!last) {
    return dateFormat(first.startTime);
  }

  return `${dateFormat(first.startTime)} - ${dateFormat(last.endTime)}`;
}

async function groupRegister() {
  if (!isValid.value || !profileFormRef.value) {
    toast.makeErrorToast(t("ERRORS.FORM_INVALID"));
    return;
  }

  await axios.post(`/training/group/${groupData.value.id}/register`, {
    ...profileFormRef.value.getFormData(),
    expectations: expectations.value,
  });

  emit("handleSuccess");
  expectations.value = null;
  toast.makeSuccessToast(t("REGISTERED"));
  visible.value = false;
}

function weekdayNames(events) {
  const days = [];

  for (const event of events.slice(0, 6)) {
    const date = new Date(event.startTime);
    const dt = moment(date, "YYYY-MM-DD");
    days.push(dt.format("dddd"));
  }

  return days.filter((x, i, a) => a.indexOf(x) === i);
}

const isUserProfileLoaded = computed(() => {
  return !!profileFormRef.value?.getFormData()?.id;
});

defineExpose({
  showRegistrationModal,
});
</script>

<style scoped>
.expectations-title {
  padding-bottom: 20px;
  display: block;
}
</style>
